import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { categories } from "@/data/categories";
import { CATEGORY_HUBS } from "@/data/categoryHubs";

/**
 * /categories — the canonical "View all product categories" index the nav
 * mega-menu links to. Lists the six core category landing pages plus the
 * five Sprint-30 hub landing pages. CollectionPage + ItemList JSON-LD.
 */
const HEROES = {
  "tabletop-dining": "https://images.unsplash.com/photo-1607062088573-f6b259c6ea0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwdGFibGUlMjBzZXR0aW5nJTIwYnJhc3MlMjBjdXRsZXJ5JTIwcGxhdGVzJTIwZGluaW5nfGVufDB8fHx8MTc5MTY1ODgyNHww&ixlib=rb-4.1.0&q=85",
  "candles-home-fragrance": "https://images.unsplash.com/photo-1773866000696-814be482f516?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxzY2VudGVkJTIwY2FuZGxlcyUyMGhvbWUlMjBmcmFncmFuY2V8ZW58MHx8fHwxNzkxNjU4ODI0fDA&ixlib=rb-4.1.0&q=85",
  "garden-outdoor": "https://images.unsplash.com/photo-1773382614211-bfe2b171f5c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxnYXJkZW4lMjBkZWNvciUyMGxhbnRlcm5zJTIwb3V0ZG9vcnxlbnwwfHx8fDE3OTE2NTg4MjR8MA&ixlib=rb-4.1.0&q=85",
  "bathroom-wellness": "https://images.unsplash.com/photo-1625940119840-585d3495dc94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtYXJibGUlMjBiYXRocm9vbSUyMGFjY2Vzc29yaWVzJTIwc29hcCUyMGRpc3BlbnNlcnxlbnwwfHx8fDE3OTE2NTg4MjR8MA&ixlib=rb-4.1.0&q=85",
  "seasonal-decor": "https://images.unsplash.com/photo-1544863308-ec385bbf5caa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxjaHJpc3RtYXMlMjBvcm5hbWVudHMlMjBkZWNvcmF0aW9ucyUyMHRyZWV8ZW58MHx8fDE3OTE2NTg4MjN8MA&ixlib=rb-4.1.0&q=85",
};

const CORE_CARDS = categories.map((c) => ({
  id: c.id,
  name: c.name,
  tagline: c.tagline,
  spec: c.spec,
  image: c.image,
}));

const HUB_CARDS = CATEGORY_HUBS.map((h) => ({
  id: h.id,
  name: h.h1.replace(/\.$/, "").replace(" sourcing from India", ""),
  tagline: h.primaryKeyword,
  spec: "Programme hub · cluster-verified · AQL 2.5",
  image: HEROES[h.id] || h.hero,
}));

const ALL_CARDS = [...CORE_CARDS, ...HUB_CARDS];

export const CategoryIndex = () => {
  const canonical = `${SITE_URL}/categories`;

  useSeo({
    title: "Product Categories · Sourcing from India | Asia Sourcing",
    description:
      "All product categories sourced from India by Asia Sourcing: lighting, furniture, home décor, textiles, brass & metalware, fashion accessories, tabletop & dining, candles & home fragrance, garden & outdoor, bathroom & wellness, seasonal décor. Cluster-verified factories, AQL 2.5 QC, export documentation.",
    path: "/categories",
    canonical,
    image: "/assets/home-decor-hub-hero.jpg",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${canonical}#collection`,
          name: "Product categories sourced from India",
          description:
            "Directory of every product category Asia Sourcing sources from India — lighting, furniture, home décor, textile, brass & metalware, fashion accessories, tabletop, candles, garden, bathroom and seasonal décor.",
          url: canonical,
          inLanguage: "en",
          provider: { "@id": `${SITE_URL}#organization` },
        },
        {
          "@type": "ItemList",
          "@id": `${canonical}#list`,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          numberOfItems: ALL_CARDS.length,
          itemListElement: ALL_CARDS.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            url: `${SITE_URL}/categories/${c.id}`,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Product Categories", path: "/categories" },
        ]),
      ],
    },
    jsonLdId: "categories-jsonld",
  });

  return (
    <div data-testid="categories-page">
      <header className="page-head wrap">
        <Reveal as="p" className="eyebrow" data-testid="categories-eyebrow">Sourcing · Product categories</Reveal>
        <WordReveal as="h1" text="Eleven categories. Fifteen clusters. One documented chain." stagger={60} testId="categories-headline" />
        <Reveal as="p" className="lede" delay={200} data-testid="categories-lede">
          Every category we develop, source and inspect from India — lighting, furniture,
          home décor, textiles, brass, fashion accessories, tabletop, candles, garden,
          bathroom and seasonal décor. Pick a category to see the clusters, the MOQs
          and the testing protocol behind it.
        </Reveal>
      </header>

      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Core categories</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 2rem", lineHeight: 1.05 }}>
          Six flagship verticals.
        </Reveal>
        <div className="products-grid" data-testid="categories-core-grid">
          {CORE_CARDS.map((c) => (
            <Reveal key={c.id}>
              <Link to={`/categories/${c.id}`} className="product-card" data-testid={`category-card-${c.id}`}>
                <img src={c.image} alt={`${c.name} sourcing — Asia Sourcing`} width="800" height="600" loading="lazy" />
                <div className="label">
                  <span className="cat">{c.tagline}</span>
                  <h3>{c.name}</h3>
                  <span className="arrow">EXPLORE →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Programme hubs</Reveal>
        <Reveal as="h2" className="display" style={{ fontSize: "var(--t-h1)", margin: "1rem 0 2rem", lineHeight: 1.05 }}>
          Five seasonal &amp; specialty hubs.
        </Reveal>
        <div className="products-grid" data-testid="categories-hub-grid">
          {HUB_CARDS.map((c) => (
            <Reveal key={c.id}>
              <Link to={`/categories/${c.id}`} className="product-card" data-testid={`category-card-${c.id}`}>
                <img src={c.image} alt={`${c.name} sourcing — Asia Sourcing`} width="800" height="600" loading="lazy" />
                <div className="label">
                  <span className="cat">{c.tagline}</span>
                  <h3>{c.name}</h3>
                  <span className="arrow">EXPLORE →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="cta wrap" style={{ borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">Tell us the category.<br />We'll find the factory.</Reveal>
        <Reveal className="row">
          <Link to="/contact" className="btn btn-fill btn-lg" data-testid="categories-cta-enquiry">Send an enquiry</Link>
          <Link to="/clusters" className="btn btn-ghost" data-testid="categories-cta-clusters">See the clusters</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default CategoryIndex;
